import { useEffect, useState } from 'react';

const OPACITY_MAX = 100;

interface UseHeaderEffectsProps {
  isTransparent: boolean;
  isSticky: boolean;
  opacityLevel: number;
}

interface UseHeaderEffectsReturn {
  isTransparencyApplied: boolean;
  isInverted: boolean;
}

/**
 * Custom hook to manage header transparency and inversion effects
 */
export const useHeaderEffects = ({
  isTransparent,
  isSticky,
  opacityLevel,
}: UseHeaderEffectsProps): UseHeaderEffectsReturn => {
  const [isTransparencyApplied, setIsTransparencyApplied] = useState(isTransparent && !isSticky);
  const [isInverted, setIsInverted] = useState(false);

  // Update transparency based on sticky state
  useEffect(() => {
    setIsTransparencyApplied(isTransparent && !isSticky);
  }, [isTransparent, isSticky]);

  // Handle DOM manipulations based on transparency and opacity
  useEffect(() => {
    const opacity = isTransparencyApplied ? opacityLevel : OPACITY_MAX;
    
    // Update inversion state based on opacity
    if (!isTransparencyApplied) {
      setIsInverted(false);
    } else if (opacity <= 50) {
      setIsInverted(true);
    }

    // Apply CSS variables and classes
    const root = document.documentElement;
    const headerElem = document.querySelector('header');

    if (isTransparent) {
      root?.style.setProperty('--header-transparency', String(opacity / OPACITY_MAX));
      headerElem?.classList.add('transparentHeaderContainer');
    }

    // Cleanup function
    return () => {
      headerElem?.classList.remove('transparentHeaderContainer');
      root?.style.removeProperty('--header-transparency');
    };
  }, [isTransparencyApplied, opacityLevel, isTransparent]);

  return { isTransparencyApplied, isInverted };
};
